import React, { useEffect, useMemo, useState } from 'react';
import { Building2, Search, Users as UsersIcon } from 'lucide-react';
import { AUDIT_PIPELINE_STAGES } from '../../../utils/auditPipelineUtils';
import { flattenGroupsToRows, resolveStageDistribution } from '../clientAuditMonitoringUtils';
import { paginate } from '../../../utils/paginationUtils';
import { matchesMonitoringInsight, resolveServiceProgramInsightLabel, resolveStandardInsightLabel } from './MonitoringInsights';
import MonitoringInsights from './MonitoringInsights';

const STAGE_INDEX = new Map(AUDIT_PIPELINE_STAGES.map((stage, index) => [stage.key, index]));
const TABULAR_NUMS = { fontVariantNumeric: 'tabular-nums' };
// 5 rows keeps this card the same height as Audit Tasks beside it in the two-up row.
const ROWS_PER_PAGE = 5;
const formatActivityTime = (timestamp) => {
  if (!timestamp) return '—';
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  }).format(new Date(timestamp));
};

/**
 * Thin stacked-bar chart of every visible project's current stage, with a color+text legend
 * (never color alone — see dataviz palette validation) beneath it. Purely additive: it summarizes
 * the table below, it never replaces any of its rows or columns.
 */
const StageDistributionChart = ({ groups, loading, selectedStage, onStageClick }) => {
  const distribution = useMemo(() => resolveStageDistribution(groups), [groups]);

  if (loading) {
    return <div className="placeholder-glow"><span className="placeholder col-12 rounded" style={{ height: 10 }} /></div>;
  }
  if (distribution.length === 0) return null;

  return (
    <div className="w-100">
      <div className="d-flex" style={{ gap: 2, height: 10 }} aria-label="Stage distribution of visible audit projects">
        {distribution.map((stage) => (
          <button
            type="button"
            key={stage.key}
            title={`${stage.key}: ${stage.count} project${stage.count === 1 ? '' : 's'} (${stage.percentage}%)`}
            aria-pressed={selectedStage === stage.key}
            onClick={() => onStageClick(selectedStage === stage.key ? null : stage.key)}
            style={{
              width: `${stage.percentage}%`, minWidth: 4, background: stage.color, borderRadius: 3,
              transition: 'filter 150ms ease-out', flexShrink: 0, border: selectedStage === stage.key ? '2px solid #0d3d42' : 'none', padding: 0,
            }}
          />
        ))}
      </div>
      <div className="d-flex flex-wrap gap-3 mt-2">
        {distribution.map((stage) => (
          <button key={stage.key} type="button" className="btn btn-link p-0 d-inline-flex align-items-center gap-1 small text-muted text-decoration-none" onClick={() => onStageClick(selectedStage === stage.key ? null : stage.key)}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: stage.color, flexShrink: 0 }} />
            {stage.shortLabel}
            <strong style={{ color: stage.color, ...TABULAR_NUMS }}>{stage.count}</strong>
          </button>
        ))}
      </div>
    </div>
  );
};

/** Compact 6-dot progress stepper — filled up to the project's current stage, in stage order. */
const MiniStepper = ({ stage }) => {
  const currentIndex = STAGE_INDEX.has(stage) ? STAGE_INDEX.get(stage) : -1;
  return (
    <div className="d-flex align-items-center gap-1" title={stage} aria-label={`Audit progress: ${stage}`}>
      {AUDIT_PIPELINE_STAGES.map((step, index) => (
        <span
          key={step.key}
          style={{
            width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
            background: index <= currentIndex ? step.color : '#e2e8f0',
            boxShadow: index === currentIndex ? `0 0 0 2px ${step.tint}` : 'none',
          }}
        />
      ))}
    </div>
  );
};

const StagePill = ({ stage }) => {
  const meta = AUDIT_PIPELINE_STAGES.find((s) => s.key === stage);
  const color = meta?.color || '#64748b';
  const tint = meta?.tint || '#f1f5f9';
  return (
    <span className="badge" style={{ background: tint, color, fontSize: '0.68rem', fontWeight: 600 }}>
      {meta?.shortLabel || stage}
    </span>
  );
};

const matchesSearch = (group, project, query) => {
  if (!query) return true;
  const haystack = `${group.companyName} ${project.projectCode} ${project.standardName || ''} ${project.salesOwnerUsername || ''}`.toLowerCase();
  return haystack.includes(query);
};

/**
 * Company -> Project audit monitoring table, shared by the Sales portfolio view, the
 * TR/Certification "broader visibility" view, and the management tier. Consumes the output of
 * groupTasksByClient — no data fetching of its own.
 */
const ClientAuditMonitoringSection = ({
  groups = [],
  loading = false,
  navigate,
  title = 'Client Audit Monitoring',
  subtitle,
  emptyLabel = 'No client audit activity to show yet.',
  insightFilter = null,
  insightRows = [],
  onInsightFilterChange,
}) => {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedStage, setSelectedStage] = useState(null);

  const filteredGroups = useMemo(() => {
    const query = search.trim().toLowerCase();
    return groups
      .filter((group) => !selectedClient || selectedClient.companyNames.includes(group.companyName))
      .map((group) => ({ ...group, projects: group.projects.filter((project) => matchesSearch(group, project, query)) }))
      .map((group) => ({ ...group, projects: group.projects.filter((project) => matchesMonitoringInsight(project, insightFilter)) }))
      .map((group) => ({ ...group, projects: selectedStage ? group.projects.filter((project) => project.stage === selectedStage) : group.projects }))
      .filter((group) => group.projects.length > 0);
  }, [groups, search, selectedClient, selectedStage, insightFilter]);

  const rows = useMemo(() => flattenGroupsToRows(filteredGroups), [filteredGroups]);

  useEffect(() => { setPage(1); }, [search, groups, selectedClient, selectedStage, insightFilter]);

  const {
    pageRows: pagedRows, currentPage, totalPages, pageStart, rangeEnd,
  } = paginate(rows, page, ROWS_PER_PAGE);

  const totalProjects = groups.reduce((sum, g) => sum + g.projectCount, 0);

  return (
    <div className="card border-0 shadow-sm w-100 client-audit-monitoring">
      <div className="card-header bg-transparent d-flex flex-wrap align-items-center gap-2">
        <div className="d-flex align-items-center gap-2 me-auto">
          <div className="rounded-2 d-flex align-items-center justify-content-center"
            style={{ width: 32, height: 32, background: 'rgba(26,118,126,.1)' }}>
            <Building2 size={16} color="#1a767e" />
          </div>
          <div>
            <h3 className="card-title fw-semibold mb-0">{title}</h3>
            {subtitle && <div className="text-muted small" style={{ textWrap: 'pretty' }}>{subtitle}</div>}
          </div>
          {!loading && (
            <span className="badge" style={{ background: 'rgba(26,118,126,.12)', color: '#1a767e', fontSize: '0.72rem', ...TABULAR_NUMS }}>
              {groups.length} clients · {totalProjects} projects
            </span>
          )}
        </div>
        <div className="input-group input-group-sm" style={{ width: 220 }}>
          <span className="input-group-text border-0 bg-transparent"><Search size={13} className="text-muted" /></span>
          <input type="text" className="form-control border-0"
            style={{ background: 'rgba(0,0,0,.04)', borderRadius: '0 8px 8px 0' }}
            placeholder="Company / project / sales…"
            value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
      </div>

      <div className="card-body pt-0 pb-3">
        <MonitoringInsights tasks={insightRows} activeFilter={insightFilter} onFilterChange={onInsightFilterChange} embedded />
        <StageDistributionChart groups={groups} loading={loading} selectedStage={selectedStage} onStageClick={setSelectedStage} />
        {(selectedClient || selectedStage || insightFilter) && <div className="mt-3 d-flex align-items-center gap-2"><span className="small text-muted">Filtered by {[selectedClient?.label, selectedStage, insightFilter?.value].filter(Boolean).join(' · ')}</span><button type="button" className="btn btn-sm btn-link p-0" onClick={() => { setSelectedClient(null); setSelectedStage(null); }}>Clear local filter</button></div>}
      </div>

      <div className="card-body p-0 border-top">
        <div className="table-responsive">
          <table className="table table-vcenter table-hover card-table mb-0">
            <thead style={{ background: 'rgba(0,0,0,.02)' }}>
              <tr>
                <th>Company / Project</th>
                <th>Sales</th>
                <th>Standard / Cycle</th>
                <th>Progress</th>
                <th>Stage</th>
                <th>Assigned</th>
                <th>Task activity</th>
                <th className="text-center">Open tasks</th>
              </tr>
            </thead>
            <tbody>
              {loading
                ? Array.from({ length: 4 }).map((_, i) => (
                  <tr key={i}>
                    {Array.from({ length: 8 }).map((__, c) => (
                      <td key={c}><div className="placeholder-glow"><span className="placeholder col-8 rounded" style={{ height: 14 }} /></div></td>
                    ))}
                  </tr>
                ))
                : rows.length === 0
                ? (
                  <tr>
                    <td colSpan={8} className="text-center py-5 text-muted">
                      <Building2 size={36} className="mb-2 opacity-25 d-block mx-auto" />
                      {groups.length === 0 ? emptyLabel : 'No clients match the current search.'}
                    </td>
                  </tr>
                )
                : pagedRows.map((project) => (
                    <tr
                      key={project.projectId}
                      role="link"
                      tabIndex={0}
                      className="client-audit-row"
                      style={{ cursor: 'pointer' }}
                      title="Open audit process"
                      onClick={() => navigate(`/audit-process/${project.projectId}`)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') navigate(`/audit-process/${project.projectId}`);
                      }}
                    >
                      <td>
                        <div className="fw-semibold text-truncate" style={{ maxWidth: 220 }} title={project.companyName}>{project.companyName}</div>
                        <div className="text-muted small fw-mono">{project.projectCode}</div>
                      </td>
                      <td className="small text-muted">{project.salesOwnerUsername || '—'}</td>
                      <td className="small">
                        <div>{resolveStandardInsightLabel(project) || '—'}</div>
                        <div className="text-muted" style={{ fontSize: '0.7rem' }}>{resolveServiceProgramInsightLabel(project) || project.auditCycle || ''}</div>
                      </td>
                      <td><MiniStepper stage={project.stage} /></td>
                      <td><StagePill stage={project.stage} /></td>
                      <td className="small">
                        {project.assignees.length === 0 ? (
                          <span className="text-muted">—</span>
                        ) : (
                          <span className="d-inline-flex align-items-center gap-1 text-muted" title={project.assignees.join(', ')}>
                            <UsersIcon size={12} />
                            {project.assignees.length === 1 ? project.assignees[0] : `${project.assignees[0]} +${project.assignees.length - 1}`}
                          </span>
                        )}
                      </td>
                      <td className="small" style={{ minWidth: 172 }}>
                        <div className="d-flex flex-column gap-1">
                          <span className="text-muted" title="First audit task created">
                            Created <strong className="text-body fw-medium">{formatActivityTime(project.taskCreatedAt)}</strong>
                          </span>
                          <span className="text-muted" title="Latest update across this project's audit tasks">
                            Updated <strong className="text-body fw-medium">{formatActivityTime(project.lastUpdated)}</strong>
                          </span>
                        </div>
                      </td>
                      <td className="text-center">
                        <span className="badge" style={{ background: project.openTaskCount > 0 ? 'rgba(234,88,12,.12)' : 'rgba(22,163,74,.12)', color: project.openTaskCount > 0 ? '#ea580c' : '#16a34a', fontSize: '0.7rem', ...TABULAR_NUMS }}>
                          {project.openTaskCount}/{project.totalTaskCount}
                        </span>
                      </td>
                    </tr>
                ))
              }
            </tbody>
          </table>
        </div>
        {!loading && rows.length > 0 && (
          <div className="card-footer bg-transparent text-muted small d-flex flex-wrap align-items-center justify-content-between gap-2">
            <span style={TABULAR_NUMS}>
              Showing <strong className="text-body">{pageStart + 1}-{rangeEnd}</strong> of <strong className="text-body">{rows.length}</strong> projects
            </span>
            <div className="d-flex align-items-center gap-1" style={{ background: 'rgba(0,0,0,.03)', borderRadius: 24, padding: 4 }}>
              <button
                type="button"
                className="btn btn-sm d-flex align-items-center justify-content-center"
                style={{
                  width: 30, height: 30, borderRadius: '50%', border: 'none', padding: 0,
                  background: currentPage <= 1 ? 'transparent' : '#fff',
                  color: currentPage <= 1 ? '#c1c7d0' : '#1a767e',
                  boxShadow: currentPage <= 1 ? 'none' : '0 1px 3px rgba(0,0,0,.12)',
                  cursor: currentPage <= 1 ? 'default' : 'pointer',
                  transitionProperty: 'background-color, box-shadow', transitionDuration: '150ms',
                }}
                disabled={currentPage <= 1}
                aria-label="Previous page"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                ‹
              </button>
              <span className="px-2 fw-semibold" style={{ fontSize: '0.78rem', color: '#1a767e', ...TABULAR_NUMS }}>
                {currentPage} <span className="text-muted fw-normal">/ {totalPages}</span>
              </span>
              <button
                type="button"
                className="btn btn-sm d-flex align-items-center justify-content-center"
                style={{
                  width: 30, height: 30, borderRadius: '50%', border: 'none', padding: 0,
                  background: currentPage >= totalPages ? 'transparent' : '#fff',
                  color: currentPage >= totalPages ? '#c1c7d0' : '#1a767e',
                  boxShadow: currentPage >= totalPages ? 'none' : '0 1px 3px rgba(0,0,0,.12)',
                  cursor: currentPage >= totalPages ? 'default' : 'pointer',
                  transitionProperty: 'background-color, box-shadow', transitionDuration: '150ms',
                }}
                disabled={currentPage >= totalPages}
                aria-label="Next page"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                ›
              </button>
            </div>
          </div>
        )}
      </div>
      <style>{`
        .client-audit-row { transition-property: background-color; transition-duration: 150ms; transition-timing-function: ease-out; }
        .client-audit-row:focus-visible { outline: 2px solid #1a767e; outline-offset: -2px; }
      `}</style>
    </div>
  );
};

export default ClientAuditMonitoringSection;
